import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutation, useQuery } from "@apollo/client/react";
import {
    AddClientNoteDocument,
    ClientNotesDocument,
    DeleteClientNoteDocument,
    UpdateClientNoteDocument,
} from "@/graphql/clients.generated";
import { toCoordinatorNote } from "@/pages/Alumni/helpers/client";

/**
 * A client's coordinator notes, and adding, editing and deleting them.
 *
 * Shared by the profile (which shows the newest few) and the full record (which shows them all):
 * the same query with the same variables, so the full record opens from cache. Every change is
 * refetched rather than written into the cache by hand — the list is newest first, and the server's
 * order is the one to trust. Each change resolves true once saved; false when it was not, and a
 * toast has said why, so the caller keeps whatever was being written.
 */
export function useClientNotes(clientId: string) {
    const { t } = useTranslation();
    const { data, loading, error } = useQuery(ClientNotesDocument, { variables: { clientId } });
    const refetch = { refetchQueries: [ClientNotesDocument] };
    const [addClientNote] = useMutation(AddClientNoteDocument, refetch);
    const [updateClientNote] = useMutation(UpdateClientNoteDocument, refetch);
    const [deleteClientNote] = useMutation(DeleteClientNoteDocument, refetch);

    const notes = useMemo(() => (data?.client?.notes ?? []).map(toCoordinatorNote), [data]);

    const addNote = async (text: string): Promise<boolean> => {
        try {
            await addClientNote({ variables: { clientId, body: text } });
            return true;
        } catch {
            toast.error(t("admin.alumni.notes.saveFailed"));
            return false;
        }
    };

    const editNote = async (noteId: string, text: string): Promise<boolean> => {
        try {
            await updateClientNote({ variables: { noteId, body: text } });
            return true;
        } catch {
            toast.error(t("admin.alumni.notes.updateFailed"));
            return false;
        }
    };

    const deleteNote = async (noteId: string): Promise<boolean> => {
        try {
            await deleteClientNote({ variables: { noteId } });
            toast.success(t("admin.alumni.notes.deleted"));
            return true;
        } catch {
            toast.error(t("admin.alumni.notes.deleteFailed"));
            return false;
        }
    };

    return {
        notes,
        /** The first load, before any notes have arrived. */
        loading: loading && !data,
        loadFailed: Boolean(error) && !data,
        addNote,
        editNote,
        deleteNote,
    };
}
